import { logger } from '../utils/logger';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { EmptyState, FigureRow } from './app/AppPage';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    Tooltip,
    CircularProgress,
    Alert,
    Button,
    Stack,
    useTheme,
    alpha,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import NexusListView from './NexusListView';
import NexusMapView from './NexusMapView';
import CompanySelector from './CompanySelector';
import { getAuthHeader } from '../utils/auth';
import { useAuth } from '../contexts/AuthContext';

interface NexusState {
    state_code: string;
    state_name: string;
    sales_threshold: number;
    current_sales: number;
    transaction_threshold?: number;
    current_transactions: number;
    percent_to_sales_threshold: number;
    percent_to_transaction_threshold?: number;
    exceeded_threshold: boolean;
    approaching_threshold: boolean;
    nexus_status: 'exceeded' | 'approaching' | 'safe';
    effective_date: string;
    lookback_period: string;
    current_period?: string;
    rule_type?: string;
    nexus_rule_triggered?: string;
    rule_explanation?: string;
}

interface MapNexusInsightsProps {
    companyId?: number;
}

const MapNexusInsights: React.FC<MapNexusInsightsProps> = ({ companyId: propCompanyId }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { companyId: urlCompanyId } = useParams();
    const [filingPeriod, setFilingPeriod] = useState<string | null>(null);
    const [companyId, setCompanyId] = useState<number | null>(() => {
        // Priority: URL > Prop > User's Primary Company
        if (urlCompanyId) return parseInt(urlCompanyId);
        if (propCompanyId) return propCompanyId;
        if (user?.primary_company_id) return user.primary_company_id;
        return null; // Only null if no company available anywhere
    });
    const [nexusData, setNexusData] = useState<NexusState[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
    const [metadata, setMetadata] = useState<any>(null);
    const [dateRangeMode, setDateRangeMode] = useState<'filing_period' | 'calendar_year' | 'all'>('calendar_year');
    const [customFilingPeriod, setCustomFilingPeriod] = useState<string | ''>('');
    const [companyInfo, setCompanyInfo] = useState<any>(null);
    const displayCompanyName = companyInfo?.name || metadata?.company_name || null;
    const displayTaxId = companyInfo?.tax_id || null;
    const displayLocation = companyInfo?.city ? `${companyInfo.city}, ${companyInfo.state}` : null;
    const currentYear = new Date().getFullYear();
    const [calendarYear, setCalendarYear] = useState<number>(currentYear);
    const [availablePeriods, setAvailablePeriods] = useState<
        Array<{ value: string; label: string; transaction_count?: number; total_amount?: number }>
    >([]);

    const yearOptions = useMemo(() => {
        const years = new Set<number>();

        const addYear = (value?: number | null) => {
            if (typeof value === 'number' && !Number.isNaN(value)) {
                years.add(value);
            }
        };

        availablePeriods.forEach((period) => {
            const year = parseInt(period.value.split('-')[0], 10);
            addYear(year);
        });

        const metadataFilingYear = metadata?.filing_period
            ? parseInt(metadata.filing_period.split('-')[0], 10)
            : NaN;
        addYear(metadataFilingYear);
        addYear(metadata?.calendar_year ?? null);
        addYear(calendarYear);

        return Array.from(years).sort((a, b) => b - a);
    }, [availablePeriods, calendarYear, metadata?.filing_period, metadata?.calendar_year]);

    // Handle company change
    const handleCompanyChange = useCallback((newCompanyId: number) => {
        logger.log('Company changed to:', newCompanyId);
        setLoading(true); // Set loading immediately when company changes
        setCompanyId(newCompanyId);
        setError(null); // Clear any previous errors
        // Update the URL to reflect the selected company
        navigate(`/company/${newCompanyId}/nexus/dashboard`);
    }, [navigate]);

    const fetchCompanyInfo = useCallback(async () => {
        try {
            logger.log('Fetching company info for ID:', companyId);
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            
            const authHeader = getAuthHeader();
            const headers = new Headers({
                'Content-Type': 'application/json',
            });
            
            // Add auth headers
            Object.entries(authHeader).forEach(([key, value]) => {
                headers.append(key, value);
            });
            
            const response = await fetch(`${API_URL}/api/v1/companies/${companyId}`, {
                headers
            });
            
            if (response.ok) {
                const company = await response.json();
                logger.log('Company info received:', company);
                setCompanyInfo(company);
            } else {
                logger.error('Failed to fetch company info:', response.status);
                setCompanyInfo(null);
            }
        } catch (err) {
            logger.error('Error fetching company info:', err);
            setCompanyInfo(null);
        }
    }, [companyId]);

    useEffect(() => {
        if (!availablePeriods.length) {
            return;
        }
        const newestYear = parseInt(availablePeriods[0].value.split('-')[0], 10);
        if (!Number.isNaN(newestYear) && !availablePeriods.some((period) => period.value.startsWith(`${calendarYear}-`))) {
            setCalendarYear(newestYear);
        }
    }, [availablePeriods, calendarYear]);

    useEffect(() => {
        if (dateRangeMode !== 'filing_period' && customFilingPeriod) {
            setCustomFilingPeriod('');
        }
    }, [dateRangeMode, customFilingPeriod]);

    const fetchNexusData = useCallback(async () => {
        logger.log('🔍 fetchNexusData called at:', new Date().toISOString(), 'for companyId:', companyId);
        try {
            setLoading(true);
            setError(null);
            
            // Direct API call to nexus insights with company_id
            try {
                const timestamp = Date.now();
                logger.log('🌐 Fetching nexus data via axios api for company:', companyId);
                const params: any = {
                    company_id: companyId,
                    _t: timestamp,
                    range_mode: dateRangeMode,
                };
                const effectivePeriod = customFilingPeriod || filingPeriod;
                if (dateRangeMode === 'filing_period' && effectivePeriod) {
                    params.filing_period = effectivePeriod;
                }
                if (dateRangeMode === 'calendar_year' && calendarYear) {
                    params.calendar_year = calendarYear;
                } else if (dateRangeMode === 'calendar_year') {
                    params.calendar_year = currentYear;
                }
                const { data } = await api.get('/api/v1/nexus/insights', { params });
                logger.log('📊 Nexus data received:', data);
                
                // Handle the new response format which includes both insights and metadata
                if (data && data.insights) {
                    setNexusData(data.insights as NexusState[]);
                    
                    // Set metadata if available
                    if (data.metadata) {
                        setMetadata({
                            filing_period: data.metadata.filing_period || '',
                            total_transactions: data.metadata.total_transactions || 0,
                            total_sales: data.metadata.total_sales || 0,
                            states_shown: data.metadata.states_shown || 0,
                            threshold_filter: data.metadata.threshold_filter || '',
                            data_source: data.metadata.data_source || '',
                            explanation: data.metadata.explanation || '',
                            date_range_label: data.metadata.date_range_label || '',
                            company_name: data.metadata.company_name || '',
                        });
                        if (Array.isArray(data.metadata.available_periods)) {
                            setAvailablePeriods(data.metadata.available_periods);
                        }
                        if (!filingPeriod && data.metadata.filing_period) {
                            setFilingPeriod(data.metadata.filing_period);
                        }
                        if (data.metadata.calendar_year && data.metadata.calendar_year !== calendarYear) {
                            setCalendarYear(data.metadata.calendar_year);
                        }
                    }
                    // If backend provides current filing period, track it
                    if (data?.metadata?.filing_period && !filingPeriod) {
                        setFilingPeriod(data.metadata.filing_period);
                    }
                } else {
                    // For backward compatibility with old API format
                    setNexusData(data as NexusState[]);
                }
                
                setLastUpdated(new Date());
                setLoading(false);
            } catch (error: any) {
                logger.error('❌ Error fetching nexus data:', error);
                const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
                setError(`Failed to fetch nexus data: ${msg}`);
                setLoading(false);
            }
        } catch (error) {
            logger.error('❌ Error in fetchNexusData:', error);
            setError(`Error: ${error instanceof Error ? error.message : String(error)}`);
            setLoading(false);
        }
    }, [companyId, filingPeriod, customFilingPeriod, dateRangeMode, calendarYear]);

    // Update companyId when user data loads (if not already set from URL/prop)
    useEffect(() => {
        if (!urlCompanyId && !propCompanyId && user?.primary_company_id && !companyId) {
            logger.log('📌 Setting company ID from user context:', user.primary_company_id);
            setCompanyId(user.primary_company_id);
        }
    }, [user, urlCompanyId, propCompanyId, companyId]);

    // Fetch data whenever companyId changes
    useEffect(() => {
        logger.log('🚀 MapNexusInsights - companyId changed:', companyId);
        if (companyId && companyId !== 0) {  // ✅ Add null/0 check
            fetchNexusData();
            fetchCompanyInfo();
        } else {
            logger.log('⏸️ Skipping fetch: no valid companyId');
            setLoading(false);
        }
    }, [companyId, fetchNexusData, fetchCompanyInfo]);

    // Statistics
    const exceededCount = nexusData.filter(s => s.nexus_status === 'exceeded').length;
    const approachingCount = nexusData.filter(s => s.nexus_status === 'approaching').length;
    const safeCount = nexusData.filter(s => s.nexus_status === 'safe').length;

    if (loading) {
        const loadingLabel = displayCompanyName
            ? `Loading nexus data for ${displayCompanyName}...`
            : companyId
                ? `Loading nexus data for company #${companyId}...`
                : 'Loading nexus data...';
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Economic Nexus Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                    {loadingLabel}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6">States Overview</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Chip label="Loading" color="default" />
                                <Chip label="Pending" color="warning" />
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle1">Map Preview</Typography>
                            <Box
                                className="leaflet-container"
                                sx={{ height: 240, mt: 1, borderRadius: 2, bgcolor: 'grey.100' }}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    // Show error if no company ID is available
    if (!companyId) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    No company selected. Please select a company to view nexus insights.
                </Alert>
            </Box>
        );
    }

    return (
        <Box className="gt-page gt-page-wide">
            {/* Header */}
            <Box className="gt-head gt-head-plain" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h4" component="h1" className="gt-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Economic nexus
                        <Tooltip title="Interactive dashboard showing tax nexus obligations across US states">
                            <InfoIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                        </Tooltip>
                    </Typography>
                    {displayCompanyName && (
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                            {displayCompanyName}
                            {displayTaxId ? `, tax ID ${displayTaxId}` : ''}
                            {displayLocation ? `, ${displayLocation}` : ''}
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Last updated: {lastUpdated.toLocaleString()}
                    </Typography>
                    {metadata?.date_range_label && (
                        <Typography variant="body2" color="text.secondary">
                            Range: {metadata.date_range_label}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <CompanySelector 
                        selectedCompanyId={companyId}
                        onCompanyChange={handleCompanyChange}
                        label="Company"
                    />

                    {/* Month selector and date range controls */}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="range-mode-label">Range</InputLabel>
                        <Select
                            labelId="range-mode-label"
                            value={dateRangeMode}
                            label="Range"
                            onChange={(e) => setDateRangeMode(e.target.value as any)}
                        >
                            <MenuItem value="filing_period">Filing Month</MenuItem>
                            <MenuItem value="calendar_year">Calendar Year</MenuItem>
                            <MenuItem value="all">All Transactions</MenuItem>
                        </Select>
                    </FormControl>

                    {dateRangeMode === 'calendar_year' && (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="calendar-year-label">Year</InputLabel>
                            <Select
                                labelId="calendar-year-label"
                                value={calendarYear}
                                label="Year"
                                onChange={(e) => setCalendarYear(Number(e.target.value))}
                            >
                                {yearOptions.length === 0 && (
                                    <MenuItem value={calendarYear}>{calendarYear}</MenuItem>
                                )}
                                {yearOptions.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {dateRangeMode === 'filing_period' && (
                        <TextField
                            label="Filing Period"
                            type="month"
                            size="small"
                            value={customFilingPeriod || filingPeriod || ''}
                            onChange={(e) => {
                                setCustomFilingPeriod(e.target.value);
                                setFilingPeriod(e.target.value);
                            }}
                            sx={{ minWidth: 170 }}
                            InputLabelProps={{ shrink: true }}
                        />
                    )}
                    {dateRangeMode !== 'filing_period' && (
                        <Typography variant="body2" color="text.secondary">
                            {dateRangeMode === 'calendar_year' ? 'Showing current calendar year' : 'Showing all transactions'}
                        </Typography>
                    )}
                    <Button
                        variant="outlined"
                        onClick={fetchNexusData}
                        startIcon={<RefreshIcon />}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert 
                    severity={error.includes('demo data') ? 'warning' : 'info'} 
                    sx={{ mb: 2 }}
                >
                    {error}
                    <Button 
                        onClick={fetchNexusData} 
                        variant="outlined" 
                        size="small" 
                        sx={{ ml: 2 }}
                    >
                        Retry
                    </Button>
                </Alert>
            )}

            {metadata && (
                <div className="gt-nexus-report">
                    <dl>
                        <div><dt>Sales in range</dt><dd>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(metadata.total_sales || 0)}</dd></div>
                        <div><dt>Transactions in range</dt><dd>{metadata.total_transactions}</dd></div>
                    </dl>
                    <details>
                        <summary>Report details</summary>
                        <p>{metadata.explanation}</p>
                        <p>Activity filter: {metadata.threshold_filter}</p>
                        <p>Filing period: {metadata.filing_period}</p>
                    </details>
                    <Button size="small" variant="outlined" onClick={() => navigate(`/company/${companyId}/transaction-insights`)}>
                        View Transaction Insights
                    </Button>
                </div>
            )}

            <Box sx={{ mb: 3 }}>
                <FigureRow items={[
                    { label: 'Exceeded', value: exceededCount, tone: exceededCount > 0 ? 'error' : undefined },
                    { label: 'Approaching', value: approachingCount, tone: approachingCount > 0 ? 'warn' : undefined },
                    { label: 'Under threshold', value: safeCount, tone: 'ok' },
                    { label: 'States monitored', value: nexusData.length },
                ]} />
            </Box>

            {/* Main Content */}
            <Paper sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
                {nexusData.length === 0 ? (
                    <EmptyState
                        title="No nexus insights for this company yet"
                        body="Nexus is measured from your sales. Bring in transactions and each state's threshold fills in here; or try Refresh, or pick a different company."
                        action={{ label: 'Connect a store', to: '/platforms/integrations' }}
                        secondary={{ label: 'See all transactions', to: '/platforms/transactions' }}
                    />
                ) : (
                    <NexusMapView nexusData={nexusData} companyId={companyId || undefined} filingPeriod={filingPeriod || undefined} />
                )}
            </Paper>
            {nexusData.length > 0 && (
                <Box component="section" aria-labelledby="nexus-state-breakdown-title" sx={{ mt: 4, minWidth: 0 }}>
                    <Typography id="nexus-state-breakdown-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        State breakdown
                    </Typography>
                    <NexusListView companyId={companyId || undefined} nexusData={nexusData} filingPeriod={filingPeriod || undefined} />
                </Box>
            )}
        </Box>
    );
};

export default MapNexusInsights; 
